// CatalogCard — `.ra-cat-card` from the redesign bundle.
// design ref: styles.css:2660–2706, bulk-request.jsx:828–851
//
// 160px min-height card with logo / title / app+catalog meta / Add | Added
// action row pinned to the bottom via margin-top: auto.
//
// On `added` we render a 20×20 success.500 dot in the top-right corner and
// flip the bottom action to "Added" in success.600. The browse grid never
// disables a card — duration-over-max is surfaced only on selected items in
// the basket drawer (useBasket.rowStatus), so users can add freely and
// adjust the duration afterwards.

import { Box } from "@chakra-ui/react";
import { TbCheck, TbMinus, TbPlus, TbSwitchHorizontal } from "react-icons/tb";
import AppLogo from "components/ds/AppLogo";
import type { FlatRequestItem } from "./types";
import { catalogLinkTenant } from "state/members/access_v2/catalogNames";

interface CatalogCardProps {
  item: FlatRequestItem;
  added: boolean;
  /** Set when the resource is in the basket via a DIFFERENT card (same
   *  resource published in another catalog): the catalog name holding the
   *  selection. Renders the "covered" treatment — dimmed, hollow check,
   *  "Added in <catalog>" — so the card that owns the request is
   *  unambiguous. Clicking still removes the resource (toggle-any). */
  coveredBy?: string;
  onToggle: () => void;
  /** The member already holds active access to this card's resource. */
  hasAccess?: boolean;
}

const CatalogCard = ({
  item,
  added,
  coveredBy,
  onToggle,
  hasAccess = false,
}: CatalogCardProps) => {
  const logoUrl = item.catalogItem.imageUrl;
  const covered = Boolean(coveredBy);
  const tenant = catalogLinkTenant(item.resource);

  return (
    <Box
      as="button"
      type="button"
      onClick={onToggle}
      aria-pressed={added}
      position="relative"
      display="flex"
      flexDirection="column"
      w="full"
      p="lg" // 16px
      gap="2xs" // 4px
      bg="neutral.0"
      borderWidth="1px"
      borderStyle={covered ? "dashed" : "solid"}
      borderColor={added ? "neutral.300" : "transparent"}
      borderRadius="component.2xl" // 12px
      minH="160px"
      textAlign="left"
      cursor="pointer"
      fontFamily="inherit"
      color="inherit"
      boxShadow="elevLow"
      // Strong ease-out for hover/focus; transform gets its own short
      // duration so scale-on-press feels snappy.
      transition="border-color 120ms cubic-bezier(0.23, 1, 0.32, 1), box-shadow 120ms cubic-bezier(0.23, 1, 0.32, 1), transform 120ms cubic-bezier(0.23, 1, 0.32, 1)"
      _hover={{ boxShadow: "elevHigh" }}
      _active={{ transform: "scale(0.98)" }}
      _focusVisible={{
        outline: "none",
        borderColor: "p.400",
        boxShadow: "focusRingSubtle",
      }}
    >
      {added && (
        <Box
          position="absolute"
          top="3"
          right="3"
          w="20px"
          h="20px"
          borderRadius="full"
          // Covered: hollow check — selected, but not the card holding it.
          bg={covered ? "transparent" : "success.500"}
          borderWidth={covered ? "1.5px" : "0"}
          borderStyle="solid"
          borderColor="success.500"
          color={covered ? "success.500" : "white"}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          aria-hidden
        >
          <TbCheck size={13} strokeWidth={3} />
        </Box>
      )}

      {/* .ra-cat-card__logo — 36×36 logo tile, 12px bottom margin.
          Covered cards dim their identity block (not the action line, which
          carries the explanation). */}
      <Box mb="md" display="flex" opacity={covered ? 0.55 : 1}>
        <AppLogo
          size="lg"
          name={item.catalogItem.name}
          logoUrl={logoUrl || undefined}
        />
      </Box>

      {/* .ra-cat-card__title — 16/700. overflowWrap so a long unbreakable
          name (slug, email-style) wraps inside the card instead of dictating
          the grid column's width. */}
      <Box
        as="span"
        fontSize="lg"
        fontWeight={700}
        color="neutral.950"
        lineHeight="1.2"
        minW={0}
        opacity={covered ? 0.55 : 1}
        sx={{ overflowWrap: "anywhere" }}
      >
        {item.resource.name}
      </Box>

      {tenant && (
        <Box
          as="span"
          fontSize="md"
          fontWeight={500}
          color="neutral.800"
          lineHeight="1.3"
          minW={0}
          opacity={covered ? 0.55 : 1}
          sx={{ overflowWrap: "anywhere" }}
        >
          {tenant}
        </Box>
      )}

      {/* .ra-cat-card__meta — 13/400 secondary */}
      <Box
        as="span"
        fontSize="smd"
        color="neutral.700"
        lineHeight="1.4"
        minW={0}
        opacity={covered ? 0.55 : 1}
        sx={{ overflowWrap: "anywhere" }}
      >
        {item.catalogName.replace(/ catalog$/i, "")} · {item.catalogItem.name}
      </Box>

      {hasAccess && (
        <Box
          as="span"
          mt="1xs"
          alignSelf="flex-start"
          display="inline-flex"
          alignItems="center"
          gap="2xs"
          px="8px"
          py="2px"
          borderRadius="full"
          bg="success.50"
          color="success.700"
          fontSize="xs"
          fontWeight={600}
          lineHeight="1.3"
        >
          <TbCheck size={12} strokeWidth={3} />
          You have access
        </Box>
      )}

      {/* .ra-cat-card__action — pinned to bottom via margin-top: auto */}
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        gap="1xs" // 6px
        fontSize="md" // 14px
        fontWeight={500}
        color={covered ? "neutral.600" : added ? "success.600" : "neutral.700"}
        mt="auto"
        pt="md" // 12px
      >
        {covered ? (
          // Clicking a covered card TRANSFERS the selection here (it doesn't
          // remove) — the switch icon carries that affordance.
          <>
            <TbSwitchHorizontal size={16} />
            Added in {coveredBy!.replace(/ catalog$/i, "")}
          </>
        ) : added ? (
          <>
            <TbMinus size={16} />
            Added
          </>
        ) : (
          <>
            <TbPlus size={16} />
            Add
          </>
        )}
      </Box>
    </Box>
  );
};

export default CatalogCard;
